import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BrowserRouter as Router } from 'react-router-dom'
import { ConfigProvider, message } from 'antd'
import type { Locale } from 'antd/lib/locale'
import enUS from 'antd/locale/en_US'
import esES from 'antd/locale/es_ES'
import plPL from 'antd/locale/pl_PL'
import viVN from 'antd/locale/vi_VN'
import dayjs from 'dayjs'
import 'dayjs/locale/es'
import 'dayjs/locale/pl'
import 'dayjs/locale/vi'
import WebChat from './modules/PatientPortal/components/WebChat/WebChat'
import ScrollToTop from './router/ScrollToTop'
import AppRoutes from './router/appRoutes'
import { useAuthStore } from './store/authStore'

message.config({
  duration: 5,
})

const App = () => {
  const token = useAuthStore((state) => state.token)
  const { i18n } = useTranslation()
  const [antdLocale, setAntdLocale] = useState<Locale>(enUS)

  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      dayjs.locale(lng)

      switch (i18n.language) {
        case 'es':
          setAntdLocale(esES)
          break
        case 'pl':
          setAntdLocale(plPL)
          break
        case 'vi':
          setAntdLocale(viVN)
          break
        default:
          setAntdLocale(enUS)
          break
      }
    }

    i18n.on('languageChanged', handleLanguageChange)

    return () => {
      i18n.off('languageChanged', handleLanguageChange)
    }
  }, [i18n, setAntdLocale])

  return (
    <ConfigProvider
      locale={antdLocale}
      theme={{
        token: {
          colorPrimary: '#0f687c',
          colorInfo: '#0f687c',
          fontFamily: 'Montserrat',
        },
        components: {
          Button: {
            primaryShadow: 'var(--color-primary-10)',
            fontWeight: 500,
            contentFontSizeSM: 14,
            contentFontSize: 14,
            contentFontSizeLG: 14,
          },
          Alert: {
            colorInfoBg: '#DEF0F4',
            colorInfoBorder: '#DEF0F4',
            colorInfo: '#0F687C',
            colorText: '#262626',
            fontWeightStrong: 600,
          },
          Progress: {
            defaultColor: '#C7E1E7',
            remainingColor: '#F5F5F5',
            colorSuccess: '#5495A4',
          },
        },
      }}
    >
      <Router>
        <ScrollToTop />
        <AppRoutes />
        {token && <WebChat />}
      </Router>
    </ConfigProvider>
  )
}

export default App
